import { IDeviceSize } from '@checkout/device';
import { IBillingAddressForm, ILocation, IPaymentFields, IPaymentMethod, IShipwallet, RootState } from '@checkout/state';
import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  AddressForm,
  CheckoutMembershipModule,
  ConsentGroupForm,
  ContactDetailsForm,
  DeliveryOptions,
  IngridWidget2,
  KlarnaCheckout,
  NewsletterCheckbox,
  PaymentOptions,
  // PersonalIdentityNumber,
  PersonalInformation,
  ShippingCountry,
} from '..';
import { CheckoutContextConfig } from '../../../apps/CheckoutApp';
import { checkoutRequest } from '../../../reducers/checkout';
import { FormNames } from '../../../utils/formNames';
import { isKlarnaCheckout, isOnlyKlarnaCheckout, isPaypal } from '../../../utils/paymentMethods';
import { Button, GroupTitle, MessageBox, Section, SvgIcon, Widget } from '../../presentational';
import messages from './../../../i18n/base-en';
import * as Styles from './FlatCheckout.scss';

interface IOwnProps {
  deviceSize: IDeviceSize;
  isValid?: boolean; // This is injected from CheckoutForm
}

interface IConnectedProps {
  isCheckoutFetching: boolean;
  paymentMethods: IPaymentMethod[];
  widgets?: any;
  location: ILocation;
  grandTotalPrice: string;
  billingAddressForm: IBillingAddressForm;
  paymentFields: IPaymentFields;
  selectedPaymentMethod: string;
  shipwallet: IShipwallet;
}

interface IConnectetDispatchProps {
  checkoutRequest: typeof checkoutRequest;
}

type Props = IOwnProps & IConnectedProps & IConnectetDispatchProps & InjectedIntlProps;

class FlatCheckout extends React.Component<Props, {}> {

  render() {
    const {
      intl,
      grandTotalPrice,
      selectedPaymentMethod,
      shipwallet,
      billingAddressForm,
      paymentFields,
      location,
      widgets,
      paymentMethods,
      isCheckoutFetching,
      deviceSize } = this.props;
    const { formatMessage } = intl;
    const selectedKlarnaCheckout = isKlarnaCheckout(selectedPaymentMethod);
    const onlyKlarnaCheckout = isOnlyKlarnaCheckout(paymentMethods);
    const isShipwalletAvailable = shipwallet !== undefined && !shipwallet.error;
    const isPaypalSelected = isPaypal(selectedPaymentMethod);
    const buttonLabel = isPaypalSelected ?
      formatMessage(messages.place_order_button_paypal_label) :
      formatMessage(messages.place_order_button_label);
    const addressShippingNote = formatMessage(messages.address_shipping_note);
    const payPalNote = formatMessage(messages.paypal_note);

    return (
      <CheckoutContextConfig.Consumer>
        {(checkoutContextConfig => (
          <div className={Styles.wrapper}>
            <div className={Styles.container}>
              {checkoutContextConfig.modules.membership.isEnabled && (
                <Section blockFor="CheckoutMembershipModule">
                  <CheckoutMembershipModule />
                </Section>
              )}
              {checkoutContextConfig.ui.cartContactTop.enabled && (
                <Section blockFor="ContactDetails" spacingBottom="no">
                  <GroupTitle title={formatMessage(messages.top_email_label)} accent={true} variant="big" />
                  <ContactDetailsForm
                    fields={paymentFields.shippingAddress}
                    formName={FormNames.contactDetails}
                    checkoutContextConfig={checkoutContextConfig}
                    isEmailAlwaysVisible={true}
                    isPhoneVisible={false}
                  />
                </Section>
              )}
              {checkoutContextConfig.ui.splitShippingCountry.isEnabled && (
                <ShippingCountry />
              )}
              {checkoutContextConfig.ui.countryDescriptions &&
                checkoutContextConfig.ui.countryDescriptions.enabled && (
                  <React.Fragment>
                    {(checkoutContextConfig.ui.countryDescriptions.descriptions[location.country]) && (
                      <MessageBox
                        type="info"
                        spacingTop={false}
                        message={checkoutContextConfig.ui.countryDescriptions.descriptions[location.country]}
                      />
                    )}
                    {(checkoutContextConfig.ui.countryDescriptions.descriptions[location.eu ? 'EU' : 'NOTEU']) && (
                      <MessageBox
                        type="info"
                        spacingTop={false}
                        message={checkoutContextConfig.ui.countryDescriptions.descriptions[(location.eu ? 'EU' : 'NOTEU')]}
                      />
                    )}
                  </React.Fragment>
                )
              }
              {!onlyKlarnaCheckout && (
                <Section blockFor="PaymentOptions">
                  <PaymentOptions displayDirection={checkoutContextConfig.ui.layout.paymentOptionsDisplayDirection} />
                </Section>
              )}
              {isShipwalletAvailable && shipwallet.version === 2 && (
                <Section blockFor="IngridWidget2">
                  <IngridWidget2 />
                </Section>
              )}
              <PersonalInformation />
              {!isShipwalletAvailable && (
                <Section blockFor="DeliveryOptions">
                  <GroupTitle title={formatMessage(messages.shipping_label)} accent={true} variant="big" />
                  <DeliveryOptions showButton={false} />
                </Section>
              )}
              {!selectedKlarnaCheckout && !isPaypalSelected && (
                <Section blockFor="BillingAddress">
                  <AddressForm
                    location={location}
                    switchable={true}
                    isActive={!billingAddressForm.billingSameAsShipping}
                    fields={paymentFields.address}
                    formName={FormNames.billingAddress}
                  />
                </Section>
              )}
              <Section blockFor="NewsletterSection">
                <NewsletterCheckbox
                  id="cartFieldsData.newsletter"
                  name="cartFieldsData.newsletter"
                  reverse={deviceSize.md}
                  sendNewsletterValue={true}
                  checkoutContextConfig={checkoutContextConfig}
                  deviceSize={deviceSize}
                />
              </Section>
              {!selectedKlarnaCheckout && (
                <Section spacingBottom={!selectedKlarnaCheckout && !isPaypalSelected ? 'no' : 'half'} blockFor="ConsentGroupForm">
                  <ConsentGroupForm deviceSize={deviceSize} />
                </Section>
              )}
              {isPaypalSelected && ( // TODO check also if all fields under paymentFields are visible
                <MessageBox
                  type="info"
                  message={`<p>${addressShippingNote}</p><p>${payPalNote}</p>`}
                />
              )}
              {selectedKlarnaCheckout && (
                <KlarnaCheckout />
              )}
              {widgets[selectedPaymentMethod] && !selectedKlarnaCheckout && (
                <Widget snippet={widgets[selectedPaymentMethod]} selectedPaymentMethod={selectedPaymentMethod} />
              )}
              {!selectedKlarnaCheckout && (
                <div className={Styles.buttonWrapper}>
                  <div className={Styles.price}>{formatMessage(messages.total_price_message, { grandTotalPrice })}</div>
                  <Button
                    align="center"
                    type="submit"
                    variant="primary"
                    iconInside={<SvgIcon icon="lock" />}
                    label={buttonLabel}
                    isLoading={isCheckoutFetching}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </CheckoutContextConfig.Consumer>
    );
  }
}

export default compose<React.ComponentClass<IOwnProps>>(
  injectIntl,
  connect<IConnectedProps, IConnectetDispatchProps>(
    (state: RootState) => ({
      paymentMethods: state.checkout.paymentMethods,
      widgets: state.checkout.widgets,
      location: state.checkout.location,
      paymentFields: state.checkout.paymentFields,
      grandTotalPrice: state.checkout.selection.totals.grandTotalPrice,
      selectedPaymentMethod: state.checkout.selection.paymentMethod,
      shipwallet: state.checkout.selection.pluginFields && state.checkout.selection.pluginFields.shipwallet,
      billingAddressForm: state.checkout.fieldsData.billingAddress,
      isCheckoutFetching: state.checkout.isCheckoutFetching,
    }),
    {
      checkoutRequest
    },
  )
)(FlatCheckout);
